import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { message, Modal } from "antd";
import {
  handleExtraActive,
} from "../../redux/slices/products";
import { selectUser } from "../../redux/slices/user";
import CondimentUI from "./components/CondimentUI";
import {
  disableMenuItemExtraAdmin,
  getCondimentGroups,
  getCondimentsByGroup,
  updateCondimentsOrder,
} from "../../api/Products";
import { useLocation, useParams } from "react-router-dom";

const Condiments = () => {
  const [loading, handleLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [condiments, setCondiments] = useState([]);
  const [condimentsLoading, setCondimentsLoading] = useState(false);
  const [selectedChannelId, setSelectedChannelId] = useState(null);

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const params = useParams();
  const user = useSelector(selectUser);
  const [visibleEditor, handleVisibleEditor] = useState(false);
  const isSucursal = pathname.includes("productos-por-sucursal") || !user.admin;

  // ----- Fetch groups (admin view) -----
  const fetchGroups = useCallback(async () => {
    handleLoading(true);
    try {
      const response = await getCondimentGroups();
      if (!response.error && response.status === 200 && response.data?.status === "success") {
        const g = response.data.groups || [];
        setGroups(g);
        // Auto-select first group
        if (g.length > 0 && !selectedGroupId) {
          setSelectedGroupId(g[0].id);
        }
      } else {
        message.error("¡Hubo un problema cargando los grupos!");
      }
    } catch (e) {
      message.error("¡Hubo un problema!");
    } finally {
      handleLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Fetch condiments for selected group -----
  const fetchCondiments = useCallback(async (groupId) => {
    if (!groupId) return;
    setCondiments([]);
    setCondimentsLoading(true);
    try {
      const branchId = isSucursal ? (params.branchId || user.branch) : null;
      const response = await getCondimentsByGroup(groupId, branchId);
      if (!response.error && response.status === 200 && response.data?.status === "success") {
        setCondiments(response.data.condiments || []);
      } else {
        message.error("¡Hubo un problema cargando los condimentos!");
      }
    } catch (e) {
      message.error("¡Hubo un problema!");
    } finally {
      setCondimentsLoading(false);
    }
  }, [isSucursal, params.branchId, user.branch]);

  // Initial load
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // When selected group changes, load its condiments
  useEffect(() => {
    if (selectedGroupId) {
      setSelectedChannelId(null);
      fetchCondiments(selectedGroupId);
    }
  }, [selectedGroupId, fetchCondiments]);

  const handleGroupSelect = (groupId) => {
    setSelectedGroupId(groupId);
  };

  // Refresh condiments after toggle
  const refreshCurrent = () => {
    fetchCondiments(selectedGroupId);
  };

  const handleExtrasActive = async (idExtra, val, type, branch, cat, userId) => {
    const response = await dispatch(handleExtraActive(idExtra, val, type, branch, cat, userId));
    if (response.status !== "success") {
      message.error("¡Hubo un problema!");
    }
  };

  const confirmVisibility = (sku, mode, active, groupId) => {
    const branchId = isSucursal ? (params.branchId || user.branch) : 0;
    Modal.confirm({
      title: isSucursal
        ? `¿${active ? 'Desactivar' : 'Activar'} para esta sucursal?`
        : `¿${active ? 'Desactivar' : 'Activar'} para todas las sucursales?`,
      okText: "Si",
      cancelText: "No",
      async onOk() {
        try {
          const response = await disableMenuItemExtraAdmin(user.id, sku, mode, groupId, branchId);
          if (response.error || response.data?.status !== "success") {
            message.error("¡Hubo un problema!");
          }
        } catch (e) {
          message.error("¡Hubo un problema!");
        } finally {
          refreshCurrent();
        }
      },
      onCancel() { },
    });
  };

  const sortedCondiments = [...condiments].sort((a, b) => {
    if (selectedChannelId) {
      const aChan = a.channels?.find(ch => ch.channel_id === selectedChannelId);
      const bChan = b.channels?.find(ch => ch.channel_id === selectedChannelId);
      const aOrder = aChan ? aChan.setOrder : a.setOrder;
      const bOrder = bChan ? bChan.setOrder : b.setOrder;
      return (aOrder || 0) - (bOrder || 0);
    }
    return (a.setOrder || 0) - (b.setOrder || 0);
  });

  const handleDragDropSku = async (draggedSku, targetSku) => {
    const dragIdx = sortedCondiments.findIndex(c => c.sku === draggedSku);
    const targetIdx = sortedCondiments.findIndex(c => c.sku === targetSku);
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;

    const list = [...sortedCondiments];
    const [draggedItem] = list.splice(dragIdx, 1);
    list.splice(targetIdx, 0, draggedItem);

    // Map new orders
    const orderList = list.map((item, idx) => ({
      sku: item.sku,
      setOrder: idx
    }));

    // Update condiments local state with new channel order values
    const updatedCondiments = condiments.map(c => {
      const orderItem = orderList.find(x => x.sku === c.sku);
      if (orderItem) {
        return {
          ...c,
          channels: c.channels.map(ch => ch.channel_id === selectedChannelId ? { ...ch, setOrder: orderItem.setOrder } : ch)
        };
      }
      return c;
    });

    setCondiments(updatedCondiments);

    // Call API
    try {
      const branchId = isSucursal ? (params.branchId || user.branch) : 0;
      const res = await updateCondimentsOrder(selectedGroupId, selectedChannelId, orderList, branchId);
      if (res.error || !res.data || res.data.status !== "success") {
        message.error("¡Hubo un problema actualizando el orden!");
        fetchCondiments(selectedGroupId);
      } else {
        message.success("¡Orden actualizado!");
        fetchCondiments(selectedGroupId);
      }
    } catch (e) {
      message.error("¡Hubo un problema actualizando el orden!");
      fetchCondiments(selectedGroupId);
    }
  };

  const closeEditor = () => handleVisibleEditor(false);
  const openEditor = () => handleVisibleEditor(true);

  return (
    <CondimentUI
      loading={loading}
      condimentsLoading={condimentsLoading}
      groups={groups}
      condiments={sortedCondiments}
      selectedGroupId={selectedGroupId}
      onGroupSelect={handleGroupSelect}
      handleExtrasActive={handleExtrasActive}
      confirmVisibility={confirmVisibility}
      visibleEditor={visibleEditor}
      closeEditor={closeEditor}
      openEditor={openEditor}
      effectiveBranch={params.branchId || user.branch}
      isSucursal={isSucursal}
      selectedChannelId={selectedChannelId}
      onChannelChange={setSelectedChannelId}
      handleDragDropSku={handleDragDropSku}
    />
  );
};

export default Condiments;
